import { createAsyncThunk } from '@reduxjs/toolkit'
import { Auth } from '@zocks-communications/client-sdk-js'
import { ACCOUNT_ID_NAME } from '../../common/constants'
import {
  confirmForgotPassword,
  confirmSignUpWithVerificationCodeConsole,
  forgotPassword,
  getConsoleTokenForAssociationCode,
  loginWithUsernamePasswordConsoleV2,
  refreshTokenRequest,
  signUpWithUsernamePasswordConsoleV2,
} from '../../api/auth'
import { Residency } from '../../models/Residency'
import { AuthenticatedTokenUser } from '../../models/User'
import { Authorization } from '../../models/Authorization'
import { saveToken, stayLoggedIn, Token } from '../token/token'

export const loginWithUsernamePasswordV2 = createAsyncThunk(
  'auth/loginWithUsernamePasswordV2',
  async (
    {
      email,
      password,
      stayLoggedIn,
      captchaToken,
    }: {
      email: string
      password: string
      stayLoggedIn: boolean
      captchaToken: string
    },
    thunkAPI,
  ) => {
    return loginWithUsernamePasswordConsoleV2(email, password, stayLoggedIn, captchaToken)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        return parseUser(data) as AuthenticatedTokenUser
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async ({ accountId, refreshToken }: { accountId: string; refreshToken: string }, thunkAPI) => {
    return refreshTokenRequest(accountId, refreshToken)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        return parseUser(data) as AuthenticatedTokenUser
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const getTokenForAssociationCode = createAsyncThunk(
  'auth/getTokenForAssociationCode',
  async (
    {
      code,
    }: {
      code: string
    },
    thunkAPI,
  ) => {
    return getConsoleTokenForAssociationCode(code, stayLoggedIn())
      .then((data: any) => {
        const {
          onboarding_enabled: onboardingEnabled,
          onboarding_code: onboardingCode,
          onboarding_email: onboardingEmail,
          onboarding_user_id: onboardingUserId,
          message,
        } = data
        const user = parseUser(data)
        return { user, onboardingEnabled, onboardingCode, onboardingEmail, onboardingUserId, message }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const signUpWithUsernamePasswordV2 = createAsyncThunk(
  'auth/signUpWithUsernamePasswordV2',
  async (
    {
      firstName,
      lastName,
      email,
      password,
      captchaToken,
      state,
      residency,
    }: {
      firstName: string
      lastName: string
      email: string
      password: string
      captchaToken: string
      state: string | null
      residency: Residency
    },
    thunkAPI,
  ) => {
    return signUpWithUsernamePasswordConsoleV2(firstName, lastName, email, password, captchaToken, state, residency)
      .then((data: any) => {
        return data
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const confirmSignUpWithVerificationCode = createAsyncThunk(
  'auth/confirmSignUpWithVerificationCode',
  async ({ email, code }: { email: string; code: string }, thunkAPI) => {
    return confirmSignUpWithVerificationCodeConsole(email, code)
      .then((data: any) => {
        const {
          onboarding_enabled: onboardingEnabled,
          onboarding_code: onboardingCode,
          onboarding_email: onboardingEmail,
          onboarding_user_id: onboardingUserId,
          onboarding_bd_source: onboardingBDSource,
          message,
        } = data
        const user = parseUser(data)
        return {
          user,
          onboardingEnabled,
          onboardingCode,
          onboardingEmail,
          onboardingUserId,
          message,
          onboardingBDSource,
        }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const forgotPasswordWithCode = createAsyncThunk(
  'auth/forgotPasswordWithCode',
  async (
    {
      email,
    }: {
      email: string
    },
    thunkAPI,
  ) => {
    return forgotPassword(email).catch((error) => {
      return thunkAPI.rejectWithValue(error)
    })
  },
)

export const confirmForgotPasswordWithCode = createAsyncThunk(
  'auth/confirmForgotPasswordWithCode',
  async ({ email, password, code }: { email: string; password: string; code: string }, thunkAPI) => {
    return confirmForgotPassword(email, password, code).catch((error) => {
      return thunkAPI.rejectWithValue(error)
    })
  },
)

const parseUser = (payload: any) => {
  const token = Token.fromPayload(payload)
  saveToken(token)
  const user = createUserFromToken(token)
  window.localStorage.setItem(ACCOUNT_ID_NAME, user.accountId)
  return user
}

export const createUserFromToken = (token: Token): AuthenticatedTokenUser => {
  const parsedToken = Auth.parseJwt(token.accessToken)
  return {
    id: parsedToken?.sub ?? '',
    accountId: parsedToken?.account_id,
    firstName: parsedToken?.firstName ?? '',
    lastName: parsedToken?.lastName ?? '',
    username: parsedToken?.username ?? '',
    name: parsedToken?.name ?? '',
    email: parsedToken?.email ?? '',
    permissions: parsedToken.authorizations
      .filter((auth: Authorization) => auth.effect === 'allow')
      .map((auth: Authorization) => auth.scopes)
      .flat(),
    phoneNumber: parsedToken?.phoneNumber ?? '',
    roles: parsedToken?.roles ?? [],
    token: token.toSerialized(),
  }
}
